"use client"
import { graphql, useQuery } from "@opensea/graphql"
import type { HeroHeaderView } from "@opensea/navigation"
import { useCallbackRef } from "@opensea/ui-kit/hooks"
import type React from "react"
import { useEffect } from "react"
import { useShallow } from "zustand/react/shallow"
import NotFound from "@/app/[locale]/[currency]/[persona]/not-found"
import { CollectionNavigationFragment } from "@/components/collection/CollectioNavigation.fragment"
import { CollectionLayoutProvider } from "@/components/collection/CollectionLayoutProvider"
import { CollectionNavigation } from "@/components/collection/CollectionNavigation"
import { CollectionPageLayoutSkeleton } from "@/components/collection/CollectionPageLayoutSkeleton"
import {
  CollectionDropProvider,
  CollectionDropProviderCollectionFragment,
  CollectionDropProviderDropFragment,
} from "@/components/collection/drops/components/CollectionDropContext"
import { DropSync } from "@/components/collection/drops/components/DropSync"
import {
  CollectionHeader,
  CollectionHeaderFragment,
} from "@/components/collection/header/CollectionHeader"
import { CollectionOverview } from "@/components/collection/overview"
import { CollectionOverviewFragment } from "@/components/collection/overview/CollectionOverview.fragment"
import {
  CollectionHeaderPreview,
  CollectionHeaderPreviewFragment,
} from "@/components/collection/overview/components/CollectionOverviewPreview/components/CollectionHeaderPreview"
import { HeroHeaderViewProvider } from "@/components/hero-header/HeroHeaderViewProvider"
import { useNavigationContainerClassNames } from "@/components/navigation/constants"
import {
  CollectionJsonLD,
  CollectionJsonLdFragment,
} from "@/components/seo/CollectionJsonLD"
import { withSuspense } from "@/components/suspense/WithSuspense"
import { DelistedContent } from "@/components/trustandsafety/DelistedContent"
import {
  useCollectionTitle,
  useCollectionTitleFragment,
} from "@/hooks/use-collection-title"
import { useIsCollectionOverviewOnlyEnabled } from "@/hooks/use-flags"
import { useSubscription } from "@/hooks/use-subscription"
import { useSyncState } from "@/hooks/use-sync-state"
import { useTopNavbarStore } from "@/stores/use-top-navbar-store"
import { COLLECTION_PAGE_LAYOUT_QUERY } from "./query"

const collectionSubscription = graphql(
  `
    subscription CollectionPageLayoutSubscription($slug: String!) {
      collectionBySlug(slug: $slug) {
        __typename
        ... on Collection {
          id
          slug
          externalUrl
          twitterUsername
          instagramUsername
          discordUrl
          floorPrice {
            pricePerItem {
              token {
                unit
                symbol
              }
            }
          }
          flags {
            isOffersEnabled
            isRarityDisabled
          }
          ...CollectionNavigation
          ...CollectionJsonLd
        }
        ... on DelistedCollection {
          slug
        }
        ... on BlacklistedCollection {
          slug
        }
        ...useCollectionTitle
        ...CollectionDropProvider_collection
        ...CollectionHeader
        ...CollectionHeaderPreview
        ...CollectionOverview
      }
    }
  `,
  [
    CollectionHeaderFragment,
    CollectionHeaderPreviewFragment,
    CollectionOverviewFragment,
    useCollectionTitleFragment,
    CollectionDropProviderCollectionFragment,
    CollectionNavigationFragment,
    CollectionJsonLdFragment,
  ],
)

const dropSubscription = graphql(
  `
    subscription CollectionPageLayoutDropSubscription($slug: String!) {
      dropBySlug(slug: $slug) {
        ...CollectionDropProvider_drop
      }
    }
  `,
  [CollectionDropProviderDropFragment],
)

type CollectionPageLayoutProps = {
  children: React.ReactNode
  collectionSlug: string
  heroHeaderView: HeroHeaderView
  isPreview?: boolean
}

export const CollectionPageLayout = withSuspense(
  ({
    collectionSlug,
    heroHeaderView,
    children,
    isPreview,
  }: CollectionPageLayoutProps) => {
    const [{ data }] = useQuery({
      query: COLLECTION_PAGE_LAYOUT_QUERY,
      variables: { collectionSlug },
      pause: !collectionSlug,
    })

    const [collection, setCollection] = useSyncState(data?.collectionBySlug)
    const [drop, setDrop] = useSyncState(data?.dropBySlug)
    const [headerRef, setHeaderRef] = useCallbackRef<HTMLDivElement>()

    const isCollectionOverviewOnlyEnabled =
      useIsCollectionOverviewOnlyEnabled(collectionSlug)

    const navigationContainerClassNames = useNavigationContainerClassNames()

    const { setVariants: setTopNavbarVariants } = useTopNavbarStore(
      useShallow(state => ({ setVariants: state.setVariants })),
    )

    useSubscription(
      {
        query: collectionSubscription,
        variables: { slug: collection?.slug ?? "" },
        pause: !collection,
        pauseOnInactivity: false,
      },
      msg => {
        setCollection(msg.collectionBySlug)
      },
    )

    useSubscription(
      {
        query: dropSubscription,
        variables: { slug: collection?.slug ?? "" },
        pause: !collection,
        pauseOnInactivity: false,
      },
      msg => {
        setDrop(msg.dropBySlug)
      },
    )

    const title = useCollectionTitle(collection)

    // Handle navbar variants based on collection state
    useEffect(() => {
      if (!collection || collection.__typename === "BlacklistedCollection") {
        // Override navbar variants to use non-collection styling for 404 pages
        setTopNavbarVariants({
          transparent: "none",
        })
      } else if (collection.__typename === "DelistedCollection") {
        // Override navbar variants to use non-collection styling for delisted pages
        setTopNavbarVariants({
          transparent: "none",
        })
      } else {
        // Reset to undefined for valid collections to allow normal hero header logic
        setTopNavbarVariants(undefined)
      }
    }, [collection, setTopNavbarVariants])

    // Clean up navbar variants when component unmounts
    useEffect(() => {
      return () => {
        setTopNavbarVariants(undefined)
      }
    }, [setTopNavbarVariants])

    // If no tabs are active, then user is on an invalid tab.
    if (!collection || collection.__typename === "BlacklistedCollection") {
      return <NotFound />
    }

    if (collection.__typename === "DelistedCollection") {
      return <DelistedContent entityType="collection" />
    }

    return (
      <>
        <title>{title}</title>
        <CollectionJsonLD collection={collection} />
        <CollectionDropProvider collection={collection} drop={drop}>
          <DropSync collectionSlug={collection.slug}>
            <CollectionLayoutProvider
              collectionSlug={collectionSlug}
              isPreview={isPreview}
            >
              <HeroHeaderViewProvider
                forceInitialView={isPreview}
                headerRef={headerRef}
                headerView={heroHeaderView}
              >
                {isPreview ? (
                  <CollectionHeaderPreview
                    collection={collection}
                    ref={setHeaderRef}
                  />
                ) : (
                  <CollectionHeader
                    collection={collection}
                    ref={setHeaderRef}
                  />
                )}
                {isCollectionOverviewOnlyEnabled ? (
                  <div className={navigationContainerClassNames} />
                ) : (
                  <CollectionNavigation collection={collection} />
                )}
                <main>
                  {isCollectionOverviewOnlyEnabled ? (
                    <CollectionOverview overview={collection} />
                  ) : (
                    children
                  )}
                </main>
              </HeroHeaderViewProvider>
            </CollectionLayoutProvider>
          </DropSync>
        </CollectionDropProvider>
      </>
    )
  },
  {
    fallback: CollectionPageLayoutSkeleton,
  },
)
